import { SelectOption } from '@common/types/common';
import { CheckboxChip } from '@components/utility/checkboxes';
import { RadioChip } from '@components/utility/chips';
import { Box, BoxProps, Divider, Typography } from '@mui/material';
import { CSSProperties } from 'react';

type ChipItem = SelectOption<string | number>;

interface ChipSelectorProps<T extends ChipItem> extends BoxProps {
    /** Background color of the chip selector container */
    backgroundColor?: CSSProperties['backgroundColor'];
    /** Title displayed above the chip selector */
    title: string;
    /** Array of chip items to be displayed */
    chips: T[];
    /** Currently selected value(s) */
    value: T['value'] | T['value'][];
    /** Callback function invoked when a chip is clicked */
    onChipClick?: (chip: T) => void;
    selector?: 'radio' | 'checkbox';
}

const ChipSelector = <T extends ChipItem>({
    title,
    backgroundColor = '#f3f4f6',
    chips,
    onChipClick,
    value,
    selector = 'radio',
    ...props
}: ChipSelectorProps<T>) => (
    <Box
        {...props}
        sx={{
            borderRadius: '8px',
            ...props.sx,
            bgcolor: backgroundColor,
        }}
    >
        <Typography className="p-4" fontFamily="museo-sans" fontSize={20}>
            {title}
        </Typography>
        <Divider />
        <div className="p-4 flex flex-wrap gap-[14px] max-h-[300px] overflow-y-auto">
            {chips.map((chip) =>
                selector === 'radio' ? (
                    <RadioChip
                        key={chip.label}
                        checked={Array.isArray(value) ? value.includes(chip.value) : value === chip.value}
                        onClick={() => onChipClick?.(chip)}
                        thick
                        {...chip}
                    />
                ) : (
                    <CheckboxChip
                        key={chip.label}
                        checked={Array.isArray(value) ? value.includes(chip.value) : value === chip.value}
                        onClick={() => onChipClick?.(chip)}
                        {...chip}
                    />
                )
            )}
        </div>
    </Box>
);

export { ChipSelector };
